import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import CartDrawer from "@/components/CartDrawer";
import CombosSection from "@/components/CombosSection";
import AboutSection from "@/components/AboutSection";
import { Search, Sparkles } from "lucide-react";

function resolveHeroImage(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) {
    return `${API}${url.replace(/^\/api/, "")}`;
  }
  return url;
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");
  const [settings, setSettings] = useState(null); // null = not loaded yet — prevents hero flicker

  useEffect(() => {
    (async () => {
      try {
        const [prodRes, setRes, combosRes] = await Promise.all([
          api.get("/products"),
          api.get("/settings"),
          api.get("/combos"),
        ]);
        setProducts(prodRes.data);
        setSettings(setRes.data);
        setCombos(combosRes.data);
      } catch {
        /* noop */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (category !== "all") list = list.filter((p) => p.category === category);
    const needle = q.trim().toLowerCase();
    if (needle)
      list = list.filter(
        (p) =>
          p.brand.toLowerCase().includes(needle) ||
          p.name.toLowerCase().includes(needle)
      );
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price_5ml - b.price_5ml);
    else if (sort === "price_desc") list = [...list].sort((a, b) => b.price_5ml - a.price_5ml);
    return list;
  }, [products, category, q, sort]);

  const counts = useMemo(() => {
    const me = products.filter((p) => p.category === "middle_eastern").length;
    const pr = products.filter((p) => p.category === "premium").length;
    return { all: products.length, middle_eastern: me, premium: pr };
  }, [products]);

  const Tabs = [
    { key: "all", label: "All", count: counts.all },
    { key: "middle_eastern", label: "Middle Eastern", count: counts.middle_eastern },
    { key: "premium", label: "Premium", count: counts.premium },
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-default)]">
      <Header instagramHandle={settings?.instagram_handle} instagramUrl={settings?.instagram_url} siteName={settings?.site_name} />
      <CartDrawer />

      {/* Hero */}
      <section className="relative pt-16 sm:pt-20">
        <div className="relative h-[70vh] min-h-[520px] overflow-hidden bg-[#0A0C11]">
          {settings && resolveHeroImage(settings.hero_image_url) && (
            <img
              src={resolveHeroImage(settings.hero_image_url)}
              alt="Luxury perfume decants"
              decoding="async"
              fetchpriority="high"
              className="absolute inset-0 w-full h-full object-cover opacity-70"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C0E14]/40 via-[#0C0E14]/60 to-[#0C0E14]" />
          <div className="absolute inset-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-16 sm:pb-24">
            <div className="max-w-2xl reveal">
              <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[var(--brand-gold)] mb-5 border border-[var(--brand-gold)]/40 px-3 py-1.5">
                <Sparkles size={14} />
                {settings?.hero_eyebrow || `by @${settings?.instagram_handle || "tamilperfumeguy_"}`}
              </div>
              <h1 className="font-headings text-4xl sm:text-5xl lg:text-6xl text-[var(--text-primary)] font-light leading-[1.05] tracking-tight">
                {settings?.hero_title || "A boutique of decanted luxury."}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[var(--text-secondary)] max-w-xl leading-relaxed">
                {settings?.hero_subtitle || "100+ fragrances. Hand-poured in 5ml & 10ml. Order in a tap — we confirm on WhatsApp."}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#catalog"
                  data-testid="hero-cta"
                  className="px-6 py-3 bg-[var(--brand-gold)] text-black text-sm font-medium tracking-wide hover:bg-[var(--brand-gold-hover)] transition-colors"
                >
                  {settings?.hero_cta_primary || "Explore Catalog"}
                </a>
                <a
                  href={`https://instagram.com/${settings?.instagram_handle || "tamilperfumeguy_"}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-3 border border-[var(--border-default)] text-sm tracking-wide text-[var(--text-primary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors"
                >
                  {settings?.hero_cta_secondary || "Follow on Instagram"}
                </a>
                <a
                  href="#about"
                  data-testid="hero-about-cta"
                  className="px-6 py-3 border border-[var(--border-default)] text-sm tracking-wide text-[var(--text-primary)] hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors"
                >
                  {settings?.about_title || "About Us"}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CombosSection combos={combos} />

      {/* Catalog */}
      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3">
              {settings?.catalog_eyebrow || "The Catalog"}
            </div>
            <h2 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-light text-[var(--text-primary)]">
              {settings?.catalog_title || "Every bottle, in a size that suits you."}
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <div className="relative w-full sm:w-60 lg:w-72">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                data-testid="search-input"
                type="text"
                placeholder="Search brand or name…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full bg-[var(--bg-surface)] border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none pl-9 pr-3 py-2.5 text-sm"
              />
            </div>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full sm:w-52 bg-[var(--bg-surface)] border border-[var(--border-default)] focus:border-[var(--brand-gold)] focus:outline-none px-3 py-2.5 text-sm cursor-pointer"
            >
              <option value="default">Sort: Featured</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[var(--border-default)] pb-4">
          {Tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setCategory(t.key)}
              className={`px-4 py-2 text-xs sm:text-sm uppercase tracking-[0.2em] transition-all border ${
                category === t.key
                  ? "border-[var(--brand-gold)] text-[var(--brand-gold)] bg-[var(--brand-gold)]/10"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-[var(--text-muted)]">({t.count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/6] bg-[var(--bg-surface)] border border-[var(--border-default)] animate-pulse"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="no-products"
            className="text-center py-24 text-[var(--text-secondary)]"
          >
            {settings?.catalog_empty_text || "No perfumes match your search."}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {filtered.map((p, idx) => (
              <ProductCard key={p.id} product={p} index={idx} />
            ))}
          </div>
        )}
      </section>

      <AboutSection settings={settings} />

      <Footer instagramHandle={settings?.instagram_handle} instagramUrl={settings?.instagram_url} siteName={settings?.site_name} tagline={settings?.footer_tagline} settings={settings || {}} />
    </div>
  );
}
